/**
 * Design Tokens - Salesforce Lightning Design System (SLDS)
 *
 * Точное соответствие цветам и стилям Salesforce Lightning.
 * Основано на официальной документации SLDS.
 *
 * Использование:
 * import tokens from './theme/designTokens';
 */

// ============================================================================
// COLORS (Цвета SLDS)
// ============================================================================

export const colors = {
  // Brand Colors - Salesforce Blue palette
  brand: {
    primary: '#0176D3',        // Lightning Blue - основной синий
    dark: '#014486',           // Темнее
    darker: '#032D60',         // Record Header background
    light: '#1B96FF',          // Светлее
    lighter: '#57A3FD',        // Ещё светлее
    hover: '#0B5CAB',          // Hover state
    active: '#032D60',         // Active/pressed
  },

  // Neutral Palette - SLDS Gray scale
  neutral: {
    white: '#FFFFFF',
    1: '#FAFAF9',              // Page background
    2: '#F3F2F2',              // Subtle background, card hover
    3: '#E5E5E5',              // Borders
    4: '#C9C9C9',              // Disabled borders
    5: '#B0B0B0',              // Placeholder, disabled icons
    6: '#969492',              // Secondary icons
    7: '#706E6B',              // Secondary text
    8: '#514F4D',              // Body text
    9: '#3E3E3C',              // Headings
    10: '#181818',             // High contrast
  },

  // Semantic Colors - SLDS States
  semantic: {
    success: {
      primary: '#2E844A',      // Green - completed, success
      light: '#45C65A',
      dark: '#194E31',
      background: '#D4EDDA',
    },
    warning: {
      primary: '#DD7A01',      // Orange/Yellow - warning, paused
      light: '#FE9339',
      dark: '#A96404',
      background: '#FFF3CD',
    },
    error: {
      primary: '#BA0517',      // Red - error, terminated
      light: '#C23934',
      dark: '#8C0A0A',
      background: '#F8D7DA',
    },
    info: {
      primary: '#0176D3',      // Blue - info, current
      background: '#D1ECF1',
    },
  },

  // Path/Progress specific colors
  path: {
    complete: '#04844B',       // Зелёный для завершённых этапов
    current: '#0176D3',        // Синий для текущего
    incomplete: '#ECEBEA',     // Светло-серый для будущих
    incompleteText: '#706E6B', // Текст для будущих этапов
  },
};

// ============================================================================
// TYPOGRAPHY (Типографика SLDS)
// ============================================================================

export const typography = {
  fontFamily: {
    primary: '"Salesforce Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    monospace: 'Consolas, Menlo, Monaco, "Courier New", monospace',
  },

  fontSize: {
    10: '0.625rem',    // 10px
    11: '0.6875rem',   // 11px - labels
    12: '0.75rem',     // 12px - small text, badges
    13: '0.8125rem',   // 13px - body small
    14: '0.875rem',    // 14px - body (base)
    16: '1rem',        // 16px - emphasized
    18: '1.125rem',    // 18px - subheading
    20: '1.25rem',     // 20px - heading
    24: '1.5rem',      // 24px - page heading
    28: '1.75rem',     // 28px - large heading
  },

  fontWeight: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// ============================================================================
// SPACING (Отступы SLDS)
// ============================================================================

export const spacing = {
  0: '0',
  1: '0.25rem',       // 4px - xxx-small
  2: '0.5rem',        // 8px - xx-small
  3: '0.75rem',       // 12px - x-small
  4: '1rem',          // 16px - small (BASE)
  5: '1.25rem',       // 20px
  6: '1.5rem',        // 24px - medium
  8: '2rem',          // 32px - large
  10: '2.5rem',       // 40px
  12: '3rem',         // 48px - x-large
  16: '4rem',         // 64px - xx-large

  // Named (SLDS naming)
  xxxSmall: '0.125rem',  // 2px
  xxSmall: '0.25rem',    // 4px
  xSmall: '0.5rem',      // 8px
  small: '0.75rem',      // 12px
  medium: '1rem',        // 16px
  large: '1.5rem',       // 24px
  xLarge: '2rem',        // 32px
  xxLarge: '3rem',       // 48px

  // Mantine compatible
  xs: '0.5rem',
  sm: '0.75rem',
  md: '1rem',
  lg: '1.5rem',
  xl: '2rem',
};

// ============================================================================
// BORDERS (Границы SLDS)
// ============================================================================

export const borders = {
  width: {
    thin: '1px',
    thick: '2px',
    thicker: '3px',
  },

  radius: {
    none: '0',
    small: '0.125rem',    // 2px
    medium: '0.25rem',    // 4px - default for inputs, buttons
    large: '0.5rem',      // 8px - cards
    pill: '15rem',        // Pills, badges
    circle: '50%',
  },

  color: {
    default: '#E5E5E5',
    hover: '#C9C9C9',
    focus: '#0176D3',
    error: '#BA0517',
    success: '#2E844A',
  },
};

// ============================================================================
// SHADOWS (Тени SLDS - очень subtle)
// ============================================================================

export const shadows = {
  none: 'none',
  card: '0 2px 2px 0 rgba(0, 0, 0, 0.1)',
  dropdown: '0 2px 3px 0 rgba(0, 0, 0, 0.16)',
  modal: '0 2px 8px 0 rgba(0, 0, 0, 0.16)',
  focus: '0 0 3px #0176D3',
  focusInset: 'inset 0 0 0 1px #0176D3',
};

// ============================================================================
// Z-INDEX
// ============================================================================

export const zIndex = {
  base: 0,
  dropdown: 7000,
  sticky: 7100,
  overlay: 8000,
  modal: 9000,
  popover: 9100,
  tooltip: 9200,
  notification: 10000,
};

// ============================================================================
// BREAKPOINTS
// ============================================================================

export const breakpoints = {
  small: '480px',
  medium: '768px',
  large: '1024px',
  xLarge: '1280px',
};

// ============================================================================
// TRANSITIONS
// ============================================================================

export const transitions = {
  duration: {
    instant: '0ms',
    fast: '150ms',
    normal: '250ms',
    slow: '400ms',
  },

  easing: {
    standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  },
};

// ============================================================================
// LAYOUT
// ============================================================================

export const layout = {
  container: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
  },

  component: {
    headerHeight: '45px',        // SLDS Global Header
    recordHeaderHeight: 'auto',
    sidebarWidth: '240px',
    sidebarCollapsedWidth: '56px',
    pathHeight: '44px',
  },
};

// ============================================================================
// HELPERS
// ============================================================================

export const getColor = (path) => {
  const keys = path.split('.');
  let value = colors;
  for (const key of keys) {
    value = value[key];
    if (value === undefined) return colors.neutral[8];
  }
  return value;
};

export const rgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Export all
export default {
  colors,
  typography,
  spacing,
  borders,
  shadows,
  zIndex,
  breakpoints,
  transitions,
  layout,
  getColor,
  rgba,
};
